import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import FieldItem from "./FieldItem";
import { api } from "../../maps/data.js";
import Paper from "@mui/material/Paper";
import FieldsDialog from "../dialogs/FieldsDialog";
import ActionBarForWidget from "../ActionBarForWidget";
import Tx from "../Tx";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { ClientJS } from "clientjs";

const WrapperStyled = styled.div`
  padding: 0 8px;
  width: calc(100% + 8px);
  margin-left: -8px;
  height: 100%;
`;

const FieldItemsStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(${(props) => props.fieldsize}px, 1fr)
  );
  gap: 10px;
`;

const FieldItemsWrapperStyled = styled.div`
  overflow: auto;
  height: calc(100% - 40px);
`;

const FieldStyled = styled.div`
  margin-bottom: 10px;
  padding: 0 10px;
`;

const STORAGE_KEY = "selectedFields";

const FieldsPlayMode = ({
  edge,
  productsDetection,
  shelvesDetection,
  indications,
  poseDetection,
  currentWeight,
  openWeightHandle,
  eventStats,
  siteId,
  onSetPlayMode,
}) => {
  const [clientInfo, setClientInfo] = useState({});
  const [fieldSize, s_fieldSize] = useState(400);
  const [selectedFields, s_selectedFields] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error("Error loading saved fields:", e);
      return {};
    }
  });
  const [fields, s_fields] = useState([]);
  const [isFieldsDialogShown, s_isFieldsDialogShown] = useState(false);

  useEffect(() => {
    const client = new ClientJS();
    const info = {
      browser: client.getBrowser(),
      browserVersion: client.getBrowserVersion(),
      os: client.getOS(),
      osVersion: client.getOSVersion(),
      device: client.getDevice(),
      screenPrint: client.getScreenPrint(),
      userAgent: client.getUserAgent(),
      fingerprint: client.getFingerprint(),
    };
    setClientInfo(info);
  }, []);

  const getFields = async () => {
    const res = await api({ type: "getFields", data: { edgeId: edge._id } });
    s_fields(res);
  };

  useEffect(() => {
    getFields();
  }, [edge?._id]);

  useEffect(() => {
    if (fields.length > 0) {
      const savedFields = sessionStorage.getItem(STORAGE_KEY);
      if (!savedFields) {
        const initialSelected = {};
        fields.forEach((field) => {
          initialSelected[field._id] = {
            selected: false,
            field: { _id: field._id, name: field.name },
          };
        });
        s_selectedFields(initialSelected);
        setTimeout(() => {
          setSitePlayMode(true);
        }, 1000);
      }
    }
  }, [fields]);

  const setSitePlayMode = useCallback(
    async (enabled) => {
      if (Object.keys(selectedFields).length === 0) return;
      const shelves = fields
        .filter((field) => selectedFields[field._id]?.selected)
        .flatMap((field) => field.shelves)
        .map((shelf) => shelf.sensorId);

      await api({
        type: "setSitePlayMode",
        data: { siteId, enabled, shelves, clientId: clientInfo.fingerprint },
      });
    },
    [selectedFields, fields, siteId, clientInfo.fingerprint]
  );

  useEffect(() => {
    if (Object.keys(selectedFields).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectedFields));
      setSitePlayMode(true);
    }
  }, [selectedFields, setSitePlayMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSitePlayMode(true);
    }, 60000);

    return () => clearInterval(interval);
  }, [setSitePlayMode]);

  const hasData = !!fields?.length;

  const handleSizeChange = (increment) => {
    s_fieldSize(Math.max(200, fieldSize + increment));
  };

  const handleFieldToggle = (fieldId) => {
    s_selectedFields((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        selected: !prev[fieldId]?.selected,
      },
    }));
  };

  return (
    <WrapperStyled>
      <Paper elevation={3} style={{ height: "100%" }}>
        <ActionBarForWidget
          titleIcon={"field"}
          title={<Tx>g.fields</Tx>}
          onFullScreenClick={() => s_isFieldsDialogShown(true)}
          showMinimalscreen={false}
          onPlusClick={() => handleSizeChange(100)}
          onMinusClick={() => handleSizeChange(-100)}
        />
        <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
          {fields.map((field) => (
            <FormControlLabel
              key={field._id}
              control={
                <Checkbox
                  checked={selectedFields[field._id]?.selected || false}
                  onChange={() => handleFieldToggle(field._id)}
                  size="small"
                />
              }
              label={`${field.name} (${field.cameras?.length || 0} cameras, ${
                field.shelves?.length || 0
              } shelves)`}
            />
          ))}
        </div>
        <FieldItemsWrapperStyled>
          <FieldItemsStyled fieldsize={fieldSize}>
            {hasData &&
              fields
                .filter((field) => selectedFields[field._id]?.selected)
                .map((field, idx) => (
                  <FieldStyled key={field._id + idx}>
                    <FieldItem
                      forceUpdateFields={getFields}
                      fieldSize={fieldSize}
                      field={field}
                      productsDetection={productsDetection}
                      shelvesDetection={shelvesDetection}
                      indications={indications}
                      currentWeight={currentWeight}
                      poseDetection={poseDetection}
                      openWeightHandle={openWeightHandle}
                      eventStats={eventStats}
                    />
                  </FieldStyled>
                ))}
          </FieldItemsStyled>
        </FieldItemsWrapperStyled>
      </Paper>
      {isFieldsDialogShown && (
        <FieldsDialog
          open={isFieldsDialogShown}
          productsDetection={productsDetection}
          shelvesDetection={shelvesDetection}
          fields={fields}
          indications={indications}
          poseDetection={poseDetection}
          onClose={() => {
            s_isFieldsDialogShown(false);
          }}
        />
      )}
    </WrapperStyled>
  );
};

export default FieldsPlayMode;
